import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  output,
  signal,
  untracked
} from '@angular/core';
import { createForm } from '@sonofabit/ng-core';
import {
  MaintenanceTimerDefaultsDto,
  MaintenanceTimerDetailDto,
  MaintenanceTimerService,
  SignedInMaintenanceTimerFinishDto
} from '@artemis-software/wr-api';
import { Validators } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { NbDialogService, NbSpinnerService, NbToastrService } from '@nebular/theme';
import { DeleteDialogComponent } from '@components/dialog/delete-dialog/delete-dialog.component';
import {
  FinishMaintenanceTimerDialogComponent
} from '@components/dialog/finish-maintenance-timer-dialog/finish-maintenance-timer-dialog.component';
import { CurrentUserService } from '@services/current-user.service';
import { Router } from '@angular/router';

@Component({
  standalone: false,
  selector: 'wr-maintenance-timer',
  templateUrl: './maintenance-timer.component.html',
  styleUrls: ['./maintenance-timer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MaintenanceTimerComponent {

  readonly maintenanceTimer = input<MaintenanceTimerDetailDto>();

  readonly buildingEquipmentId = input.required<string>();

  readonly showCancelButton = input(true);

  /** Shows the TGA name as a header linking to the equipment detail page (maintenance timer dashboard). */
  readonly showEquipmentLink = input(false);

  readonly editCancel = output<void>();

  readonly update = output<MaintenanceTimerDetailDto>();

  readonly delete = output<void>();

  TypeEnum = MaintenanceTimerDetailDto.TypeEnum;

  maintenanceTimerService = inject(MaintenanceTimerService);

  nbDialogService = inject(NbDialogService);

  spinnerService = inject(NbSpinnerService);

  nbToastrService = inject(NbToastrService);

  currentUserService = inject(CurrentUserService);

  readonly router = inject(Router);

  isLoading = signal(false);

  editMode = signal(false);

  isEditing = computed(() => this.editMode() || !this.maintenanceTimer());

  currentUser = this.currentUserService.currentUser;

  /** Snapshot of the timer the currently open edit form was built from — a fresh
   * object per startEdit(), so cancelled edits are discarded and a background
   * list refresh does not rebuild (and thereby wipe) an open form. */
  private readonly formBasis = signal<{ timer: MaintenanceTimerDetailDto | undefined }>({ timer: undefined });

  private readonly now = signal(new Date());

  private readonly defaults = signal<MaintenanceTimerDefaultsDto | undefined>(undefined);

  private defaultsLoadedFor: string | undefined;

  readonly selectedType = signal<MaintenanceTimerDetailDto.TypeEnum>(MaintenanceTimerDetailDto.TypeEnum.Maintenance);

  readonly lockedConfig = computed(() =>
    this.defaults()?.lockedConfigs.find(config => config.timerType === this.selectedType()));

  readonly hasLockedConfigs = computed(() => (this.defaults()?.lockedConfigs.length ?? 0) > 0);

  readonly canEdit = computed(() => this.maintenanceTimer()?.permissions?.canEdit ?? false);

  readonly equipmentName = computed(() => this.maintenanceTimer()?.buildingEquipment.equipment.name);

  readonly sectionText = computed(() => this.maintenanceTimer()?.buildingEquipment.sectionText);

  readonly canEditWarranty = computed(() => this.currentUserService.hasPrivilege('canEditWarrantyMaintenanceTimers'));

  readonly canEditTimer = computed(() =>
    this.canEdit()
    && (this.maintenanceTimer()?.type !== MaintenanceTimerDetailDto.TypeEnum.Warranty || this.canEditWarranty()));

  readonly canFinishTimer = computed(() => this.maintenanceTimer()?.permissions?.canFinish ?? false);

  readonly canShowHistory = computed(() =>
    this.currentUserService.hasPrivilege('viewInternalMaintenanceTimers')
    || this.currentUserService.hasPrivilege('viewExternalMaintenanceTimers'));

  readonly daysLeft = computed(() => {
    const maintenanceTimer = this.maintenanceTimer();
    if (!maintenanceTimer) return 0;
    const nextDate = new Date(maintenanceTimer.nextDate);
    const diffTime = nextDate.getTime() - this.now().getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  });

  readonly expired = computed(() => !!this.maintenanceTimer() && this.daysLeft() <= 0);

  form = computed(() => {
    const maintenanceTimer = this.formBasis().timer;
    return createForm(({ field }) => {
      return {
        id: field({
          value: maintenanceTimer?.id,
        }),
        buildingEquipmentId: field({
          value: this.buildingEquipmentId(),
        }),
        type: field({
          value: maintenanceTimer?.type ?? MaintenanceTimerDetailDto.TypeEnum.Maintenance,
        }),
        startDate: field({
          value: maintenanceTimer ? new Date(maintenanceTimer.startDate) : new Date(),
          validators: [Validators.required],
          toDto: (value: Date) => value.toISOString(),
        }),
        durationDays: field({
          value: maintenanceTimer?.durationDays,
          validators: [Validators.required, Validators.min(1)],
          // Validators.required guarantees a value before getMappedValue runs in save().
          toDto: (value?: number) => value!,
        }),
        reminderDurationDays: field({
          value: maintenanceTimer?.reminderDurationDays ?? 0,
        }),
        earlyToleranceDays: field({
          value: maintenanceTimer?.earlyToleranceDays ?? 0,
        }),
        lateToleranceDays: field({
          value: maintenanceTimer?.lateToleranceDays ?? 0,
        }),
        imagesMandatory: field({
          value: maintenanceTimer?.imagesMandatory ?? true,
        }),
      };
    });
  });

  get nextDate() {
    const { startDate, durationDays } = this.form().getRawValue();

    if (startDate == undefined || durationDays === undefined) return;

    const nextDate = new Date(startDate.getTime());
    nextDate.setDate(nextDate.getDate() + durationDays);

    return nextDate;
  }

  get reminderDate() {
    const nextDate = this.nextDate;
    const reminderDurationDays = this.form().getRawValue().reminderDurationDays;

    if (nextDate == undefined || reminderDurationDays === undefined) return;

    const nextReminderDate = new Date(nextDate.getTime());
    nextReminderDate.setDate(nextReminderDate.getDate() - reminderDurationDays);

    return nextReminderDate;
  }

  get earlyToleranceDate() {
    const nextDate = this.nextDate;
    const earlyToleranceDays = this.form().getRawValue().earlyToleranceDays;
    if (nextDate == undefined || earlyToleranceDays == undefined) return;
    const date = new Date(nextDate.getTime());
    date.setDate(date.getDate() - earlyToleranceDays);
    return date;
  }

  get lateToleranceDate() {
    const nextDate = this.nextDate;
    const lateToleranceDays = this.form().getRawValue().lateToleranceDays;
    if (nextDate == undefined || lateToleranceDays == undefined) return;
    const date = new Date(nextDate.getTime());
    date.setDate(date.getDate() + lateToleranceDays);
    return date;
  }

  constructor() {
    const intervalId = setInterval(() => this.now.set(new Date()), 60_000);
    inject(DestroyRef).onDestroy(() => clearInterval(intervalId));

    effect(() => {
      if (!this.isEditing()) return;
      const buildingEquipmentId = this.buildingEquipmentId();
      untracked(() => this.loadDefaults(buildingEquipmentId));
    });

    effect(() => {
      const form = this.form();
      const defaults = this.defaults();
      untracked(() => {
        const type = form.controls.type.value;
        this.selectedType.set(type);
        if (!defaults) return;
        if (this.isNewTimer()) {
          this.applyConfig(type);
        }
        this.updateConfigLock();
      });
    });
  }

  private isNewTimer(): boolean {
    return !this.formBasis().timer;
  }

  private loadDefaults(buildingEquipmentId: string) {
    if (this.defaultsLoadedFor === buildingEquipmentId) return;
    this.defaultsLoadedFor = buildingEquipmentId;
    this.maintenanceTimerService.findDefaultsByBuildingEquipmentId(buildingEquipmentId).subscribe({
      next: defaults => this.defaults.set(defaults),
      error: () => this.defaultsLoadedFor = undefined,
    });
  }

  onTypeChange(type: MaintenanceTimerDetailDto.TypeEnum) {
    this.selectedType.set(type);
    this.applyConfig(type);
    this.updateConfigLock();
  }

  private applyConfig(type: MaintenanceTimerDetailDto.TypeEnum) {
    const config = this.defaults()?.configs.find(it => it.timerType === type);
    if (!config) return;
    this.form().patchValue({
      durationDays: config.durationDays,
      reminderDurationDays: config.reminderDurationDays,
      earlyToleranceDays: config.earlyToleranceDays,
      lateToleranceDays: config.lateToleranceDays,
      imagesMandatory: config.imagesMandatory,
    });
  }

  private updateConfigLock() {
    const controls = this.form().controls;
    const configControls = [
      controls.startDate,
      controls.durationDays,
      controls.reminderDurationDays,
      controls.earlyToleranceDays,
      controls.lateToleranceDays,
      controls.imagesMandatory,
    ];
    if (!this.lockedConfig()) {
      configControls.forEach(control => control.enable());
      return;
    }
    this.applyConfig(this.selectedType());
    if (this.isNewTimer()) {
      controls.startDate.setValue(new Date());
    }
    configControls.forEach(control => control.disable());
  }

  async save() {
    this.form().markAllAsTouched();
    if (this.form().invalid) return;
    this.isLoading.set(true);
    try {
      const dto = await this.form().getMappedValue();
      const entity = await firstValueFrom(this.maintenanceTimerService.merge(dto));
      this.editMode.set(false);
      this.update.emit(entity);
      this.nbToastrService.success('Wartungstimer wurde erfolgreich gespeichert.', 'Wartungstimer');
    } catch (e) {
      console.error(e);
    } finally {
      this.isLoading.set(false);
    }
  }

  cancelEditing() {
    this.editMode.set(false);
    this.editCancel.emit();
  }

  async deleteEntity() {
    const shouldDelete = await firstValueFrom(this.nbDialogService.open(DeleteDialogComponent, {
      context: {
        title: 'Wartungstimer löschen',
        message: 'Bist du sicher, dass du den Wartungstimer löschen willst?',
      },
    }).onClose);
    if (shouldDelete) {
      const result = firstValueFrom(this.maintenanceTimerService.deleteById(this.maintenanceTimer()!.id));
      this.spinnerService.registerLoader(result);
      await result;
      this.delete.emit();
      this.nbToastrService.success('Wartungstimer wurde erfolgreich gelöscht.', 'Wartungstimer');
    }
  }

  finishMaintenanceTimer() {
    const currentUserId = this.currentUser()?.id;

    if (!currentUserId) {
      this.nbToastrService.warning(
        'Es konnte ihr derzeitige Benutzer nicht gefunden werden, bitte melden Sie sich an.',
        'Benutzer nicht gefunden'
      );
      return;
    }

    const maintenanceTimer = this.maintenanceTimer();

    if (!maintenanceTimer) {
      return;
    }

    const dialogRef = this.nbDialogService.open(FinishMaintenanceTimerDialogComponent, {
      context: {
        title: 'Bilder für Wartung hochladen',
        mandatory: maintenanceTimer.imagesMandatory,
        userId: currentUserId,
      },
    });

    dialogRef.onClose.subscribe((result) => {
      if (!result) {
        return;
      }

      const comment = result.comment ?? '';
      const attachments = result.attachments ?? undefined;

      if (maintenanceTimer.imagesMandatory && attachments.length === 0) {
        this.nbToastrService.warning(
          'Bitte laden Sie mindestens ein Bild hoch, um den Wartungstimer abzuschließen.',
          'Bild erforderlich'
        );
        return;
      }

      const finishMaintenanceTimerDto = {
        maintenanceTimerId: maintenanceTimer.id,
        comment,
        attachments,
      } as SignedInMaintenanceTimerFinishDto;

      this.maintenanceTimerService
        .finishMaintenanceTimer(finishMaintenanceTimerDto)
        .subscribe({
          next: (entity) => {
            this.update.emit(entity);
            this.nbToastrService.success(
              'Wartungstimer wurde erfolgreich abgeschlossen.',
              'Wartungstimer'
            );
          },
          error: () => {
            this.nbToastrService.danger('Fehler beim Abschluss des Wartungstimers.', 'Fehler');
          },
        });
    });
  }

  showMaintenanceHistory() {
    const maintenanceTimer = this.maintenanceTimer();

    if (!maintenanceTimer) {
      this.nbToastrService.danger(
        'Beim Laden dieses Wartungstimers ist ein Fehler aufgetreten!',
        'Wartungstimer Fehler'
      );
      return;
    }

    const historyRoute = this.currentUserService.hasPrivilege('viewInternalMaintenanceTimers')
      ? '/maintenanceTimers/historyOverview'
      : '/external/maintenanceTimers/historyOverview';

    this.router.navigate(
      [historyRoute],
      { queryParams: { maintenanceTimerId: maintenanceTimer.id } }
    );

  }

  goToBuildingEquipment() {
    this.router.navigate(['/buildingEquipment', this.buildingEquipmentId()]);
  }

  startEdit() {
    this.formBasis.set({ timer: this.maintenanceTimer() });
    this.editMode.set(true);
  }
}
